/**
 * Pure helpers for the dashboard soil-temperature graph
 * (`SoilTemperatureGraphContainer`).
 *
 * The bug they pin: the graph filters `historical_data.soil_temp.values` by a
 * "days back" range that defaulted to 7 days for every field. An *archived*
 * field is by definition past its season, so its newest soil-temp sample is
 * almost always older than 7 days — the filter dropped every sample, the
 * container's `reloadGraph` early-returned on the empty array, and the graph
 * painted nothing. The range dropdown is hidden in archive mode, so there was
 * no way to widen it from the UI either. `soilTempDefaultDaysBack` makes
 * archive start at the full history ("Max"), mirroring what the water-potential
 * graphs already do (`FieldDetailsWPGraph` / `WaterPotentialGraphContainer`
 * both jump to their max range when `field_state === 'archive'`).
 *
 * Kept dependency-free (no dayjs, no model imports) so they stay pure and
 * runnable under `node --test`. Anything timezone-aware — the cutoff instant
 * and turning a timestamp into a dayjs date — stays in the container.
 */

const FIELD_STATE_ARCHIVE = 'archive';

// 0 means "Max": no lower bound on the timestamp filter. Matches the
// `{ days: 0, key: 'max' }` entry in the container's DATE_FILTER_OPTIONS.
const MAX_DAYS_BACK = 0;
const DEFAULT_DAYS_BACK = 7;

// Ranges longer than this get every other sample dropped before the data
// reaches Chart.js (SensorGraph.spacedData shrinks further on top of that).
const HALVE_SAMPLES_ABOVE_DAYS = 182;

const isArchiveFieldState = (fieldState) => fieldState === FIELD_STATE_ARCHIVE;

/**
 * Range the graph opens on for a given field state: full history for archived
 * fields (their data is old and the range dropdown is hidden), one week for
 * active ones.
 */
const soilTempDefaultDaysBack = (fieldState) =>
  isArchiveFieldState(fieldState) ? MAX_DAYS_BACK : DEFAULT_DAYS_BACK;

/**
 * The `{ timestamp, value }` points to plot, ascending by timestamp.
 *
 * Drops samples older than `cutoffTimestamp` (pass 0 for "Max"), samples whose
 * key is not a number, and samples whose value is null/undefined/unparseable.
 */
const selectSoilTempSamplePoints = (values, cutoffTimestamp = 0) => {
  if (!values) {
    return [];
  }
  return Object.keys(values)
    .map((key) => ({
      timestamp: parseInt(key, 10),
      value: parseFloat(values[key]),
    }))
    .filter(
      (point) =>
        !isNaN(point.timestamp) &&
        !isNaN(point.value) &&
        point.timestamp >= cutoffTimestamp,
    )
    .sort((a, b) => a.timestamp - b.timestamp);
};

const shouldHalveSoilTempSamples = (daysBack) =>
  daysBack > HALVE_SAMPLES_ABOVE_DAYS;

const halveSoilTempSamples = (points) =>
  points.length > 1 ? points.filter((_, index) => index % 2 === 0) : points;

module.exports = {
  FIELD_STATE_ARCHIVE,
  MAX_DAYS_BACK,
  DEFAULT_DAYS_BACK,
  HALVE_SAMPLES_ABOVE_DAYS,
  isArchiveFieldState,
  soilTempDefaultDaysBack,
  selectSoilTempSamplePoints,
  shouldHalveSoilTempSamples,
  halveSoilTempSamples,
};
